import React from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Building2, FileText, CheckCircle2, PrinterCheck, MailCheck, Globe } from "lucide-react";
import { IconButton } from "../../../components/ButtonComponents";

interface InvoiceViewDialogProps {
    invoice: any;
    isOpen: boolean;
    onClose: () => void;
}

export const InvoiceViewDialog: React.FC<InvoiceViewDialogProps> = ({ invoice, isOpen, onClose }) => {
    if (!invoice) return null;

    const isSales = invoice.direction === 'OUT';
    const currency = invoice.currency || 'TRY';
    const exchangeRate = invoice.exchangeRate || 1;
    const isForeign = currency !== 'TRY';

    const header = (
        <div className="flex justify-between items-center w-full pr-8">
            <div className="flex items-center gap-2">
                <FileText className="text-blue-600" size={20} />
                <span className="text-lg font-bold">Fatura Detayı: {invoice.number || '#NO-REF'}</span>
            </div>
            <div className="flex gap-2">
               <IconButton icon={PrinterCheck} onClick={() => window.print()} title="Yazdır"/> 
               <IconButton icon={MailCheck} title="E-Posta Gönder" />  
            </div>
        </div>
    );

    return (
        <Dialog  
            header={header}
            visible={isOpen} 
            onHide={onClose} 
            maximized
            modal 
            className="invoice-view-dialog"
        >
            <div className="max-w-5xl mx-auto">
                {/* Renkli Şerit */}
                <div className={`h-2 rounded-t-xl ${isSales ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                
                <div className="shadow-lg rounded-t-none card overflow-hidden print:shadow-none print:border-none  ">
                    <div className="p-8">
                        {/* Logo ve Fatura No */}
                        <div className="flex justify-between items-start mb-10">
                            <div className="flex gap-4 items-center">
                                <div className={`p-3 rounded-xl ${isSales ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`}>
                                    <Building2 size={32} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black tracking-tight uppercase">LOGONUZ BURAYA</h2>
                                    <p className="text-[10px] font-bold uppercase tracking-widest  ">Resmi Belge</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full font-bold text-[10px] mb-2 ${isSales ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                                    <CheckCircle2 size={12} /> {isSales ? 'SATIŞ FATURASI' : 'ALIŞ FATURASI'}
                                </div>
                                <h3 className="text-2xl font-mono tracking-tighter block font-bold  ">
                                    {invoice.number || '#NO-REF'}
                                </h3>
                            </div>
                        </div>

                        {/* Bilgi Kartları */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                            <div className="card2  ">
                                <label className="text-[10px] font-black uppercase tracking-widest block mb-3  ">Müşteri / Cari Bilgileri</label>
                                <h4 className="text-lg font-bold mb-1  ">{invoice.account?.name}</h4>
                                <div className="text-sm space-y-1  ">
                                    <p><span className="font-medium">Vergi No:</span> {invoice.account?.taxNumber || '-'}</p>
                                    <p><span className="font-medium">E-posta:</span> {invoice.account?.email || '-'}</p>
                                    <p><span className="font-medium">Telefon:</span> {invoice.account?.phone || '-'}</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="card2 p-3 border rounded-xl">
                                    <label className="text-[9px] font-bold uppercase block mb-1  ">Fatura Tarihi</label>
                                    <span className="text-sm font-bold">{new Date(invoice.date).toLocaleDateString('tr-TR')}</span>
                                </div>
                                <div className="card2 p-3 border rounded-xl">
                                    <label className="text-[9px] font-bold uppercase block mb-1  ">Vade Tarihi</label>
                                    <span className="text-sm font-bold text-orange-600">{new Date(invoice.dueDate || invoice.date).toLocaleDateString('tr-TR')}</span>
                                </div>
                                <div className="card2 p-3 border rounded-xl">
                                    <label className="text-[9px] font-bold uppercase block mb-1  ">Para Birimi / Kur</label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-blue-600">{currency}</span>
                                        {isForeign && <span className="text-[10px] font-mono  ">(@{exchangeRate})</span>}
                                    </div>
                                </div>
                                <div className="card2 p-3 border rounded-xl">
                                    <label className="text-[9px] font-bold uppercase block mb-1  ">Belge Durumu</label>
                                    <span className="text-[10px] font-black text-emerald-600 uppercase">Onaylandı</span>
                                </div>
                            </div>
                        </div>

                        {/* Kalemler Tablosu */}
                        <DataTable 
                            value={invoice.stockMovements} 
                            className="p-datatable-sm mb-10 border rounded-xl overflow-hidden shadow-sm"
                            rowHover
                        >
                            <Column header="#" body={(_, opt) => opt.rowIndex + 1} style={{ width: '3rem', textAlign: 'center' }} />
                            <Column header="ÜRÜN / HİZMET" body={(row) => (
                                <div className="flex flex-col">
                                    <span className="font-bold ">{row.stock?.name}</span>
                                    {row.variant?.name && <span className="text-[9px] bg-blue-50   px-1 rounded w-fit font-bold uppercase mt-1">{row.variant.name}</span>}
                                </div>
                            )} />
                            <Column header="MİKTAR" body={(row) => <span className="font-medium">{row.quantity}</span>} align="center" />
                            <Column 
                                header="BİRİM FİYAT" 
                                body={(row) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(row.unitPrice)} 
                                align="right" 
                            />
                            <Column header="KDV" body={(row) => `%${row.taxRate}`} align="center" />
                            <Column 
                                header="TOPLAM" 
                                body={(row) => (
                                    <span className="font-bold  ">
                                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(row.totalAmount)}
                                    </span>
                                )} 
                                align="right" 
                            />
                        </DataTable>

                        {/* Alt Toplamlar */}
                        <div className="flex flex-col md:flex-row justify-between items-start gap-8">
                            <div className="flex-1 card2    w-full">
                                <label className="text-[10px] font-bold uppercase text-gray-400 flex items-center gap-1 mb-2">
                                    <Globe size={10}/> Açıklama / Notlar
                                </label>
                                <p className="text-xs italic leading-relaxed  ">
                                    {invoice.description || "Herhangi bir açıklama bulunmamaktadır."}
                                </p>
                            </div>
                            
                            <div className="w-full md:w-80 space-y-3">
                                <div className="flex justify-between px-2 text-sm  ">
                                    <span className="font-medium">Ara Toplam</span>
                                    <span className="font-bold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(invoice.baseAmount)}</span>
                                </div>
                                <div className="flex justify-between px-2 text-sm   ">
                                    <span className="font-medium">Toplam KDV</span>
                                    <span className="font-bold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(invoice.taxAmount)}</span>
                                </div>
                                {invoice.discountAmount > 0 && (
                                    <div className="flex justify-between px-2 text-sm  ">
                                        <span className="font-medium">İskonto</span>
                                        <span className="font-bold">-{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(invoice.discountAmount)}</span>
                                    </div>
                                )}
                                
                                <div className={`flex justify-between items-center card2`}>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-black uppercase tracking-widest  ">Genel Toplam</span>
                                        {isForeign && (
                                            <span className="text-[9px] font-bold  ">TL Karşılığı: {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(invoice.amount * exchangeRate)}</span>
                                        )}
                                    </div>
                                    <span className={`text-2xl font-black italic ${isSales ? 'text-emerald-700' : 'text-blue-700'}`}>
                                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(invoice.amount)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};